import { useEffect, useState } from "react";
import { ArrowUpRight, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { playHoverSwoosh } from "@/components/shared/ambient-sound";
import { useNews } from "@/hooks/useNews";
import { Skeleton } from "@/components/ui/skeleton";

export const Hero = () => {
  const [query, setQuery] = useState("");
  const [activeStory, setActiveStory] = useState(0);
  const navigate = useNavigate();
  const { news, loading } = useNews();
  const featuredNews = news.slice(0, 4);

  useEffect(() => {
    if (!featuredNews.length) return;
    const timer = window.setInterval(() => {
      setActiveStory((current) => (current + 1) % featuredNews.length);
    }, 4200);
    return () => window.clearInterval(timer);
  }, [featuredNews.length]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // mock — no backend
  };
  const date = new Date();

  const currentStory =
    featuredNews.length > 0
      ? featuredNews[activeStory] || featuredNews[0]
      : null;

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full bg-[#050505] flex flex-col justify-between overflow-hidden grain pt-24 pb-10"
    >
      {/* Radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 50% at 20% 110%, rgba(255,69,0,0.22), transparent 60%), radial-gradient(ellipse 60% 40% at 90% 10%, rgba(255,69,0,0.08), transparent 70%)",
        }}
      />

      {/* Top micro-line */}
      <div
        data-testid="hero-ticker"
        className="relative z-10 font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-bone-muted px-6 md:px-10 lg:px-16 flex flex-wrap items-center gap-x-8 gap-y-2"
      >
        <span className="text-flare">● LIVE</span>
        <span>{`>>`}</span>
        <span className="hidden md:inline">
          Delhi / Noida / Gurugram / Faridabad
        </span>
        <span className="hidden md:inline ml-auto">
          {date.toLocaleString("en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </span>
      </div>

      {/* Middle — headline and featured news */}
      <div className="relative z-10 px-6 md:px-10 lg:px-16 mt-12 md:mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
          <div className="lg:col-span-7">
            <p
              data-testid="hero-eyebrow"
              className="font-mono text-[11px] md:text-xs uppercase tracking-[0.3em] text-flare mb-6"
            >
              // A MEDIA PLATFORM FOR NCR FOUNDERS
            </p>
            <h1
              data-testid="hero-headline"
              className="font-display font-extrabold text-6xl md:text-8xl lg:text-[104px] leading-[0.9] tracking-tighter text-bone max-w-[14ch]"
            >
              Startups are <br className="hidden md:block" />
              not just <span className="italic font-normal">built.</span>
              <br />
              They are <span className="text-flare">lived.</span>
            </h1>
            <p
              data-testid="hero-subtext"
              className="mt-8 max-w-xl text-base md:text-lg text-bone-muted leading-relaxed"
            >
              Stories, people, and ideas shaping Delhi NCR's startup ecosystem —
              raw, unfiltered, and deeply lived-in.
            </p>
          </div>

          <div
            data-testid="hero-news-slider"
            className="lg:col-span-5 border border-white/10 bg-black/40 backdrop-blur-sm"
          >
            {loading ? (
              <div className="aspect-[4/3] p-4 flex flex-col justify-end">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2 mt-2" />
              </div>
            ) : featuredNews.length > 0 && currentStory ? (
              <button
                type="button"
                onClick={() => navigate(`/news/${currentStory.slug}`)}
                className="group block w-full text-left"
              >
                <div
                  className="relative aspect-[4/3] bg-coverlg  overflow-hidden bg-black"
                  style={{ backgroundImage: `url(${currentStory.cover})` }}
                >
                  <img
                    key={currentStory.id}
                    src={currentStory.cover}
                    alt={currentStory.title}
                    className="h-full w-full backdrop-blur-xl  object-contain grayscale-color animate-fade-up"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent" />
                  <div className="absolute left-4 top-4 flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] bg-flare text-black px-2 py-1">
                      Featured
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] bg-black text-flare px-2 py-1">
                      {currentStory.tag}
                    </span>
                  </div>
                </div>
                <div className="p-4 bg-gradient-to-t from-black/20 to-transparent">
                  <h3 className="font-display font-bold text-lg leading-tight group-hover:text-flare transition-colors">
                    {currentStory.title}
                  </h3>
                  <div className="flex items-center justify-between mt-3 text-xs">
                    <span className="font-mono uppercase tracking-[0.2em] text-bone-muted">
                      by {currentStory.author}
                    </span>
                    <div className="flex items-center gap-2 font-mono uppercase tracking-[0.2em] text-bone-muted group-hover:text-flare transition-colors">
                      Read story
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
              </button>
            ) : (
              <div className="aspect-[4/3] p-8 flex flex-col justify-center items-center text-center">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone-muted">
                  No featured news at the moment.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom — search bar */}
      {/* <div className="relative z-10 px-6 md:px-10 lg:px-16 mt-16 md:mt-20">
        <form
          onSubmit={handleSubmit}
          data-testid="hero-search-form"
          className="group border-t border-white/15 pt-6"
        >
          <div className="flex items-end gap-4 border-b-2 border-white/15 focus-within:border-flare pb-3 transition-colors">
            <Search className="w-5 h-5 md:w-6 md:h-6 text-bone-muted shrink-0 mb-2" />
            <input
              data-testid="hero-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search founders, stories, or ideas..."
              className="flex-1 bg-transparent font-display text-2xl md:text-4xl lg:text-5xl text-bone placeholder:text-white/20 outline-none py-2 min-w-0"
            />
            <button
              type="submit"
              data-testid="hero-search-submit"
              onMouseEnter={playHoverSwoosh}
              className="shrink-0 font-mono text-xs uppercase tracking-[0.2em] text-bone hover:text-flare transition-colors flex items-center gap-2 pb-3"
            >
              Ask <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 font-mono text-[10px] uppercase tracking-[0.2em]">
            <span className="text-bone-muted">Try:</span>
            {[
              "Gurugram SaaS",
              "Female founders",
              "Pre-seed Delhi",
              "AI agents",
            ].map((t) => (
              <button
                type="button"
                key={t}
                data-testid={`hero-search-chip-${t.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setQuery(t)}
                className="border border-white/15 px-3 py-1 hover:border-flare hover:text-flare transition-colors"
              >
                {t}
              </button>
            ))}
          </div>
        </form>
      </div> */}
    </section>
  );
};
